import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { formatKobo } from "../lib/format";
import { ALLERGEN_CHIPS, ALLERGY_DISCLAIMER } from "../lib/allergy";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function Checkout() {
  const { items, subtotal, lineTotal, clear } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const zones = settings?.zones || [];

  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", zone_id: "", notes: "" });
  const [allergyChoice, setAllergyChoice] = useState(null);
  const [allergyTags, setAllergyTags] = useState([]);
  const [allergyNote, setAllergyNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);

  const selectedZone = zones.find((z) => z.id === form.zone_id);
  const deliveryFee = selectedZone?.fee || 0;
  const total = subtotal + deliveryFee;
  const minOrder = settings?.min_order_value || 0;
  const belowMin = minOrder && subtotal < minOrder;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const placeOrder = async () => {
    if (!form.name || !form.phone || !form.email || !form.address || !form.zone_id) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (allergyChoice === null) {
      toast.error("Please answer the food allergy question.");
      return;
    }
    if (allergyChoice === "yes" && allergyTags.length === 0 && !allergyNote.trim()) {
      toast.error("Please tell us about the allergy (select at least one or add a note).");
      return;
    }
    if (belowMin) {
      toast.error(`Minimum order value is ${formatKobo(minOrder)}.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer: { name: form.name, phone: form.phone, email: form.email, address: form.address },
        zone_id: form.zone_id,
        notes: form.notes,
        allergy_flag: allergyChoice === "yes",
        allergy_tags: allergyChoice === "yes" ? allergyTags : [],
        allergy_note: allergyChoice === "yes" ? allergyNote.trim() : null,
        items: items.map((p) => ({
          item_id: p.item_id,
          variant: p.variant,
          quantity: p.quantity,
          addons: p.addons || [],
          special_instructions: p.special_instructions || "",
        })),
      };
      const { data } = await api.post("/orders", payload);
      setPendingOrder(data);
      setPayOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPayment = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/payments/mock/${pendingOrder.id}`);
      clear();
      setPayOpen(false);
      navigate(`/confirmation/${data.reference}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl text-sapore-purple mb-3">Your cart is empty</h1>
        <button onClick={() => navigate("/menu")} className="mt-4 bg-sapore-gold text-sapore-ink font-semibold px-6 py-3 rounded-full hover:bg-sapore-goldHover">
          Browse the menu
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-sapore-purple mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-5">
          <div className="bg-white rounded-2xl border border-sapore-border p-6">
            <h2 className="font-serif text-xl text-sapore-ink mb-4">Delivery details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name *" testid="checkout-name" value={form.name} onChange={(v) => update("name", v)} />
              <Field label="Phone number *" testid="checkout-phone" value={form.phone} onChange={(v) => update("phone", v)} />
              <Field label="Email address *" testid="checkout-email" value={form.email} onChange={(v) => update("email", v)} type="email" className="sm:col-span-2" />
              <Field label="Delivery address *" testid="checkout-address" value={form.address} onChange={(v) => update("address", v)} className="sm:col-span-2" />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-sapore-ink mb-1.5">Delivery zone *</label>
                <select
                  data-testid="checkout-zone"
                  value={form.zone_id}
                  onChange={(e) => update("zone_id", e.target.value)}
                  className="w-full rounded-xl border border-sapore-border p-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sapore-purple/30"
                >
                  <option value="">Select your zone</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.id}>{z.name} — {formatKobo(z.fee)}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-sapore-ink mb-1.5">Delivery note (optional)</label>
                <textarea
                  data-testid="checkout-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  rows={2}
                  className="w-full rounded-xl border border-sapore-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sapore-purple/30"
                />
              </div>
            </div>
            <div className="mt-4 bg-sapore-goldSubtle border border-sapore-gold/40 rounded-xl p-3 text-sm text-sapore-inkSoft">
              We'll confirm your estimated delivery time by WhatsApp shortly after your order is placed.
            </div>
          </div>

          <div className="bg-white rounded-2xl border-2 border-sapore-purple p-6" data-testid="allergy-block">
            <h2 className="font-serif text-xl text-sapore-purple mb-1">Does anyone eating this order have a food allergy?</h2>
            <p className="text-xs text-sapore-muted mb-4">A quick answer keeps our kitchen safe — please choose one.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              {[["no", "No allergies"], ["yes", "Yes — please note the following"]].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  data-testid={`allergy-${val}`}
                  onClick={() => setAllergyChoice(val)}
                  className={`flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                    allergyChoice === val ? "bg-sapore-purple text-sapore-cream border-sapore-purple" : "bg-white text-sapore-ink border-sapore-border hover:border-sapore-purple"
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full border-2 ${allergyChoice === val ? "border-sapore-cream bg-sapore-gold" : "border-sapore-muted"}`} />
                  {label}
                </button>
              ))}
            </div>

            {allergyChoice === "yes" && (
              <div className="mt-4" data-testid="allergy-details">
                <div className="flex flex-wrap gap-2 mb-3">
                  {ALLERGEN_CHIPS.map((chip) => {
                    const active = allergyTags.includes(chip);
                    return (
                      <button
                        key={chip}
                        type="button"
                        data-testid={`allergy-chip-${chip}`}
                        onClick={() => setAllergyTags((prev) => (active ? prev.filter((c) => c !== chip) : [...prev, chip]))}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-sapore-gold text-sapore-ink border-sapore-gold" : "bg-white text-sapore-ink border-sapore-border hover:border-sapore-gold"}`}
                      >
                        {chip}
                      </button>
                    );
                  })}
                </div>
                <textarea
                  data-testid="allergy-note"
                  value={allergyNote}
                  onChange={(e) => setAllergyNote(e.target.value.slice(0, 300))}
                  rows={2}
                  placeholder="e.g. severe peanut allergy — please keep separate"
                  className="w-full rounded-xl border border-sapore-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sapore-purple/30"
                />
                <p className="text-xs text-sapore-muted mt-1">Anything else we should know ({allergyNote.length}/300)</p>
              </div>
            )}

            <p className="text-xs text-sapore-muted mt-4 leading-relaxed">{ALLERGY_DISCLAIMER}</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-sapore-border p-6 sticky top-28">
            <h2 className="font-serif text-xl text-sapore-ink mb-4">Order summary</h2>
            <ul className="space-y-3 max-h-64 overflow-y-auto no-scrollbar">
              {items.map((p) => (
                <li key={p.key} className="flex justify-between text-sm">
                  <span className="text-sapore-inkSoft">
                    {p.quantity}× {p.name}
                    {p.variant ? ` (${p.variant})` : ""}
                  </span>
                  <span className="font-medium">{formatKobo(lineTotal(p))}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-sapore-border mt-4 pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-sapore-inkSoft">Subtotal</span><span>{formatKobo(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-sapore-inkSoft">Delivery{selectedZone ? ` (${selectedZone.name})` : ""}</span><span>{formatKobo(deliveryFee)}</span></div>
              <div className="flex justify-between font-bold text-lg pt-2"><span>Total</span><span data-testid="checkout-total" className="text-sapore-gold">{formatKobo(total)}</span></div>
            </div>
            {belowMin && <p className="text-xs text-red-600 mt-3">Minimum order is {formatKobo(minOrder)}. Add {formatKobo(minOrder - subtotal)} more.</p>}
            <button
              data-testid="checkout-submit-button"
              disabled={submitting || belowMin}
              onClick={placeOrder}
              className="w-full mt-5 py-3.5 rounded-full bg-sapore-purple text-sapore-cream font-semibold hover:bg-sapore-purpleDark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />}
              Pay {formatKobo(total)}
            </button>
            <p className="text-[11px] text-sapore-muted text-center mt-3">Secure payment · Full payment upfront</p>
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm" data-testid="mock-payment-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-sapore-purple flex items-center gap-2">
              <CreditCard size={22} /> Payment
            </DialogTitle>
          </DialogHeader>
          <div className="bg-sapore-goldSubtle border border-sapore-gold/40 rounded-xl p-3 text-xs text-sapore-inkSoft mb-2">
            Demo payment — Paystack will replace this in production. No real charge is made.
          </div>
          <p className="text-sm text-sapore-inkSoft">Order <strong>{pendingOrder?.reference}</strong></p>
          <p className="text-2xl font-bold text-sapore-gold">{formatKobo(pendingOrder?.total || 0)}</p>
          <button
            data-testid="confirm-payment-button"
            disabled={submitting}
            onClick={confirmPayment}
            className="w-full mt-3 py-3.5 rounded-full bg-sapore-whatsapp text-white font-semibold hover:bg-sapore-whatsappDark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
            Complete Payment
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-sapore-ink mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-sapore-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sapore-purple/30"
      />
    </div>
  );
}
